import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { z } from "zod";
import { useTranslation } from "react-i18next";
import { PublicNav } from "@/components/PublicNav";
import { PublicFooter } from "@/components/PublicFooter";

export const Route = createFileRoute("/reset-password")({
  head: () => ({
    meta: [
      { title: "New password — VISIKT" },
      {
        name: "description",
        content:
          "Set a new password for your VISIKT account to regain access to your subscription and salary dashboard.",
      },
      { property: "og:title", content: "New password — VISIKT" },
      {
        property: "og:description",
        content: "Reset your VISIKT password and get back into your financial dashboard.",
      },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: ResetPasswordPage,
});

function ResetPasswordPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const parsed = z.string().min(8, t("auth.passwordMin")).max(128).safeParse(password);
    if (!parsed.success) {
      toast.error(parsed.error.issues[0].message);
      return;
    }
    setLoading(true);
    const { error } = await supabase.auth.updateUser({ password: parsed.data });
    setLoading(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success(t("auth.passwordUpdated"));
    navigate({ to: "/dashboard", replace: true });
  }

  return (
    <div className="min-h-screen bg-background flex flex-col px-5 max-w-md mx-auto w-full">
      <header className="pt-8 pb-12 flex items-center justify-between gap-3">
        <span className="font-display font-extrabold tracking-tighter text-xl">VISIKT</span>
        <PublicNav />
      </header>

      <main className="flex-1">
        <h1 className="text-3xl font-display font-bold tracking-tight mb-2">
          {t("auth.newPasswordHeadline")}
        </h1>
        <p className="text-sm text-muted-foreground mb-8">{t("auth.newPasswordBody")}</p>
        <form onSubmit={handleSubmit} className="space-y-4">
          <input
            type="password"
            required
            minLength={8}
            maxLength={128}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={t("auth.newPasswordPlaceholder")}
            className="input-base"
          />
          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 rounded-md bg-accent text-primary font-medium text-sm disabled:opacity-50"
          >
            {loading ? t("common.saving") : t("auth.savePassword")}
          </button>
        </form>
      </main>
      <PublicFooter />
    </div>
  );
}
